import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    Grid,
    TextField,
    makeStyles,
    FormControl,
    FormControlLabel,
    Switch,
} from '@material-ui/core';
import { KeyboardDatePicker } from '@material-ui/pickers';
import { format, isValid } from 'date-fns';
import { RichTextEditor, showToast } from '@klpkonsern/ui';

import { Heading, Block, ActionButton, Loader } from '@klp/design';
import { parseSafeDate } from '@klp/utils/dateTimeFormatter';
import { contextSelectors } from '@klp/contextSelector';
import {
    usePostKommentarMutation,
    useLazyGetKommentarQuery,
} from '../services/rtkApi';
import { kommentarerSelectors } from '../store/kommentarerSlice';

type NyKommentarProps = {
    onCancel: () => void;
    onSave: () => void;
};

const useStyles = makeStyles(() => ({
    titleContainer: {
        width: '100%',
    },
    title: {
        width: 'inherit',
    },
    datePicker: {
        maxWidth: '300px',
    },
}));

export const RedigerKommentar = ({ onCancel, onSave }: NyKommentarProps) => {
    const { t } = useTranslation();
    const {
        selectedDate: kommentarerSelectedDate,
        editMode: { kommentarId },
    } = useSelector(kommentarerSelectors);
    const { selectedContext } = useSelector(contextSelectors);
    const [text, setText] = useState<string>('');
    const [title, setTitle] = useState<string>('');
    const [errors, setErrors] = useState<{ text: boolean; title: boolean }>({
        text: false,
        title: false,
    });
    const [selectedDate, setSelectedDate] = useState<Date>(
        kommentarerSelectedDate,
    );
    const classes = useStyles();

    const handleDateChange = (date: Date) => {
        if (!isValid(date)) {
            return;
        }
        setSelectedDate(date);
    };

    const [framhevetKommentar, setFremhevetKommentar] =
        useState<boolean>(false);

    const [postKommentar, { isError: postKommentarError }] =
        usePostKommentarMutation();

    const [
        getKommentar,
        {
            data: kommentarData,
            isLoading: kommentarDataLoading,
            isFetching: kommentarDataFetching,
        },
    ] = useLazyGetKommentarQuery();

    const handleEditKommentar = async () => {
        if (!kommentarData) return;

        const newPost = {
            ...kommentarData,
            tittel: title,
            tekst: text,
            framhevet: framhevetKommentar,
            gjelderForAar: format(selectedDate, 'yyyy'),
        };

        await postKommentar({ ...newPost });

        if (postKommentarError) {
            showToast({ body: t('general.error.message'), type: 'error' });
        }
    };

    const handleGetKommentar = async () => {
        if (!selectedContext?.id) return;

        const payload = await getKommentar({
            byggNummer: selectedContext.id,
            id: kommentarId,
        }).unwrap();

        setTitle(payload?.tittel ?? '');
        setText(payload?.tekst ?? '');
        if (payload?.framhevet) {
            setFremhevetKommentar(payload?.framhevet);
        }
        if (payload?.gjelderForAar) {
            setSelectedDate(
                parseSafeDate(payload?.gjelderForAar) ?? new Date(),
            );
        }
    };

    useEffect(() => {
        handleGetKommentar();
    }, [selectedContext]);

    useEffect(() => {
        if (title?.length > 50 || title?.length < 1) {
            setErrors((currentErrors) => {
                return { ...currentErrors, title: true };
            });
        } else {
            setErrors((currentErrors) => {
                return { ...currentErrors, title: false };
            });
        }
    }, [title]);

    console.log('kommentar :: ', kommentarData?.tekst);

    return (
        <Block paddingRight="compact">
            <Loader isLoading={kommentarDataLoading || kommentarDataFetching}>
                <>
                    <Heading
                        klpHeadingLevel="THREE"
                        translation="comments.editComment"
                        data-cy="AddCommentView__Title"
                    />
                </>
                <Block
                    className={classes.titleContainer}
                    paddingTop="compact"
                    paddingBottom="fat"
                >
                    <TextField
                        className={classes.title}
                        required={true}
                        label={t('comments.titleLabel')}
                        id="outlined-required"
                        defaultValue={kommentarData?.tittel}
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        error={errors.title}
                        helperText={
                            errors.title &&
                            t('comments.validation.titleOutOfRange')
                        }
                    />
                </Block>
                <Block
                    className={classes.datePicker}
                    paddingTop="tiny"
                    paddingBottom="compact"
                >
                    <KeyboardDatePicker
                        fullWidth={true}
                        allowKeyboardControl={true}
                        views={['year']}
                        label={t('comments.datePickerLabel')}
                        variant="inline"
                        inputVariant="outlined"
                        InputAdornmentProps={{
                            position: 'end',
                        }}
                        value={selectedDate}
                        onChange={(date: Date | null) => {
                            if (date) handleDateChange(date as Date);
                        }}
                        maxDate={new Date()}
                        autoOk={true}
                    />
                </Block>
                {kommentarData?.tekst && (
                    <RichTextEditor
                        initialContent={kommentarData?.tekst as string}
                        onContentChange={(content) => setText(content)}
                        enableSourceEditing={true}
                        enableTables={true}
                    />
                )}
                <Block paddingTop="compact">
                    <FormControl>
                        <FormControlLabel
                            control={
                                <Switch
                                    defaultChecked={framhevetKommentar}
                                    checked={framhevetKommentar}
                                    onChange={(e) =>
                                        setFremhevetKommentar(e.target.checked)
                                    }
                                    name="comments.highlight"
                                />
                            }
                            label={t('comments.highlight')}
                        />
                    </FormControl>
                </Block>

                <Grid container={true} justifyContent="flex-end">
                    <Block paddingTop="compact" paddingBottom="fat">
                        <Grid item={true}>
                            <ActionButton
                                disabled={errors.title}
                                actionType="save"
                                translation="general.save"
                                onClick={() => {
                                    handleEditKommentar();
                                    onSave();
                                }}
                            />
                        </Grid>
                    </Block>
                    <Block
                        paddingTop="compact"
                        paddingLeft="compact"
                        paddingBottom="fat"
                    >
                        <Grid item={true}>
                            <ActionButton
                                actionType="cancel"
                                onClick={onCancel}
                                translation="general.cancel"
                            />
                        </Grid>
                    </Block>
                </Grid>
            </Loader>
        </Block>
    );
};
